import { AdjustmentCard, AdjustmentProposal, buildCard, canPreview, contractKey, readProposal } from './available-adjustment-model';
import { AvailableAdjustmentsReply } from './available-adjustment-operations';

export interface IndexedAdjustments {
    proposals: Map<string, AdjustmentProposal>;
    availableIds: Set<string>;
}

// Boundary parsing is done once per unique item, including a repeated target.
// Only the opportunities list contributes to the banner count.
export function indexAdjustments(reply: AvailableAdjustmentsReply, owner: string, portfolio: string): IndexedAdjustments {
    const proposals = new Map<string, AdjustmentProposal>();
    (reply?.opportunities || []).forEach(dto => {
        if (!proposals.has(dto.actionItemId)) { proposals.set(dto.actionItemId, readProposal(dto, owner, portfolio)); }
    });
    const availableIds = new Set(Array.from(proposals).filter(x => canPreview(x[1])).map(x => x[0]));
    if (reply?.target && !proposals.has(reply.target.actionItemId)) {
        proposals.set(reply.target.actionItemId, readProposal(reply.target, owner, portfolio));
    }
    return {proposals, availableIds};
}

export function missingAdjustments(cards: AdjustmentCard[], proposals: Map<string, AdjustmentProposal>, expanded: boolean): AdjustmentCard[] {
    return cards.filter(c => !proposals.has(c.itemId) && (expanded || c.solution.state.previewed));
}

interface CardUpdate {
    card: AdjustmentCard;
    proposal: AdjustmentProposal;
    currentlyAvailable: boolean;
    message: string;
}
export function adjustmentReason(p: AdjustmentProposal): string {
    return p.health === 'unconfirmed' ? 'Waiting For Confirmed Market and Saved Hedge Data.'
        : p.reason === 'source-changed' ? 'The Saved Hedge Has Changed.' : 'This Adjustment is no Longer Available.';
}

// Pure planning: existing cards/solutions and input proposals are not mutated.
// The panel applies these small updates to retain the rendered card identity.
export function reconcileAdjustmentCards(cards: AdjustmentCard[], proposals: Map<string, AdjustmentProposal>,
                                         expanded: boolean, newSolutionId: () => string): CardUpdate[] {
    const previousById = new Map(cards.map(c => [c.itemId, c]));
    const updates: CardUpdate[] = [];
    for (const [itemId, proposal] of Array.from(proposals)) {
        const previous = previousById.get(itemId);
        const sameConversion = !previous || sameTransaction(previous.proposal, proposal);
        const ownsPreview = previous && (previous.solution.state.previewed || previous.busy);
        const available = canPreview(proposal) && (!ownsPreview || sameConversion);
        // A historical Action Board target is not a current suggestion. Only a card
        // already being viewed (or owning a Preview) may survive loss of availability.
        if (!available && (!previous || (!expanded && !previous.solution.state.previewed && !previous.busy))) { continue; }
        if (previous && previous.proposal.transaction.length > 0 && (sameConversion || ownsPreview)
            && (expanded || previous.solution.state.previewed || previous.busy)
            && (previous.currentlyAvailable || !available || previous.solution.state.previewed || previous.busy)) {
            const frozen = available && previous.proposal.health === 'unconfirmed'
                ? {...previous.proposal, health: proposal.health, availability: proposal.availability} : previous.proposal;
            updates.push({card: previous, proposal: frozen, currentlyAvailable: available,
                message: previous.busy ? previous.message : available ? '' : adjustmentReason(proposal)});
        } else {
            const card = buildCard(itemId, proposal, previous?.solution.id || newSolutionId());
            updates.push({card, proposal, currentlyAvailable: available, message: available ? '' : adjustmentReason(proposal)});
        }
    }
    for (const card of missingAdjustments(cards, proposals, expanded)) {
        updates.push({card, proposal: card.solution.state.previewed ? card.proposal : {...card.proposal, health: 'unconfirmed'},
            currentlyAvailable: false, message: 'The Adjustment Cannot be Confirmed. Try Again.'});
    }
    return updates;
}

// Price and spot refreshes do not change the proposed contracts. A changed conversion
// may replace an unpreviewed card, but cannot silently replace an owned matrix Preview.
function sameTransaction(a: AdjustmentProposal, b: AdjustmentProposal): boolean {
    const legs = (p: AdjustmentProposal) => JSON.stringify([p.before, p.transaction, p.after]
        .map(xs => xs.map(l => [contractKey(l.ticker), l.quantity]).sort((x, y) => String(x[0]).localeCompare(String(y[0])))));
    return a.holdingsFingerprint === b.holdingsFingerprint && a.sourceKey === b.sourceKey
        && a.groupId === b.groupId && legs(a) === legs(b);
}
